/**
 * RNC — TestSend
 *
 * Admin "test send to me" form. Uses `apiClient.retail.notifications.dispatch`
 * with a transactional category so opt-out rules are bypassed.
 *
 * Role-gate: editor (SuperAdmin + Compliance).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, FilterChips, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { NotificationChannel } from '@retail-ops/shared-types/enums';
import type { DispatchNotificationRequest } from '@retail-ops/shared-types';
import { canEditRnc } from './roles';

export default function TestSend(): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();

  const editMode = canEditRnc(user?.primaryRole);
  const templates = config.notificationTemplates?.templates ?? [];

  const [templateName, setTemplateName] = useState<string>(
    templates[0]?.templateName ?? '',
  );
  const [channel, setChannel] = useState<NotificationChannel>('email');
  const [recipient, setRecipient] = useState<string>(user?.upn ?? '');
  const [variables, setVariables] = useState<string>('{}');
  const [sending, setSending] = useState<boolean>(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!editMode) {
    return (
      <div className="rnc-page">
        <div className="rnc-banner rnc-banner--warn">
          Test send requires the SuperAdmin or Compliance role.
        </div>
        <div style={{ marginTop: 'var(--sp-6)' }}>
          <Button variant="ghost" onClick={() => navigate('/rnc')}>
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  const handleSend = async (): Promise<void> => {
    if (sending || !templateName) return;
    setSending(true);
    setResult(null);
    setError(null);

    let parsedVars: Record<string, unknown> = {};
    try {
      const maybe: unknown = JSON.parse(variables);
      if (maybe && typeof maybe === 'object') {
        parsedVars = maybe as Record<string, unknown>;
      }
    } catch {
      setError('Variables must be valid JSON (use {} for none).');
      setSending(false);
      return;
    }

    const req: DispatchNotificationRequest = {
      templateName,
      recipient: {
        email: channel === 'email' ? recipient : undefined,
        phone: channel === 'sms' ? recipient : undefined,
      },
      variables: parsedVars,
      channel,
      category: 'transactional',
      bypassOptOut: true,
    };

    try {
      const res = await client.retail.notifications.dispatch(req);
      if (res.success) {
        setResult(
          `Dispatched. ${res.notificationIds.length} notification id(s) created.`,
        );
      } else {
        setError(res.error ?? 'Dispatch failed.');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Dispatch failed.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rnc-page">
      <header className="rnc-page__header">
        <div className="rnc-page__title-row">
          <h1 className="rnc-page__title">Test send</h1>
          <Pill variant="muted">Admin tool</Pill>
        </div>
        <p className="rnc-page__subtitle">
          Fires a single transactional notification to yourself so you can
          verify wording / tokens. Bypasses opt-out.
        </p>
      </header>

      <div className="rnc-editor">
        <div className="rnc-editor__fields">
          <label className="rnc-editor__field-label">
            Template
            <select
              className="rnc-input"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t.templateName} value={t.templateName}>
                  {t.templateName} · v{t.currentVersion}
                </option>
              ))}
              {templates.length === 0 && (
                <option value="">No templates loaded</option>
              )}
            </select>
          </label>
          <label className="rnc-editor__field-label">
            Recipient
            <input
              className="rnc-input"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              maxLength={160}
            />
          </label>
          <div className="rnc-editor__field-label">
            Channel
            <FilterChips
              options={[
                { value: 'email', label: 'Email' },
                { value: 'teams', label: 'Teams' },
                { value: 'sms', label: 'SMS' },
              ]}
              selected={channel}
              onChange={(v) => setChannel(v as NotificationChannel)}
            />
          </div>
          <label className="rnc-editor__field-label" style={{ gridColumn: '1 / -1' }}>
            Variables (JSON)
            <textarea
              className="rnc-input rnc-textarea"
              value={variables}
              onChange={(e) => setVariables(e.target.value)}
            />
          </label>
        </div>

        {error && <div className="rnc-banner rnc-banner--err">{error}</div>}
        {result && <div className="rnc-banner rnc-banner--ok">{result}</div>}

        <div className="rnc-editor__actions">
          <Button variant="ghost" onClick={() => navigate('/rnc')}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={sending || !templateName}
          >
            {sending ? 'Sending…' : 'Send test'}
          </Button>
        </div>
      </div>
    </div>
  );
}
